import React, { useState, useCallback, useEffect, useRef } from "react";
import { createPortal } from "react-dom";

import { useMsal } from "@azure/msal-react";

import { FileText, Mail, Download, Eye } from "lucide-react";

import { callApi } from "../api/api";

import ResponsiveTableCards from "../components/ResponsiveTableCards";
import ScrollableTable from "../components/ScrollableTable";
import { resolveAdlsPath } from "../utils/resolveAdlsPath";

const POLICY_SUGGEST_MIN_LENGTH = 2;
const POLICY_SUGGEST_MAX = 5;
const POLICY_SUGGEST_DEBOUNCE_MS = 300;

function normalizePolicyQueryParam(value) {
  const trimmed = String(value || "").trim();

  if (!trimmed) {
    return "";
  }

  return trimmed.replace(/\s+/g, "").toUpperCase();
}

function transformSearchResults(apiResults) {
  const grouped = {};

  apiResults.forEach((r) => {
    if (!grouped[r.msg_id]) {
      grouped[r.msg_id] = [];
    }

    grouped[r.msg_id].push(r);
  });

  const transformed = [];

  Object.values(grouped).forEach((items) => {
    const first = items[0];

    if (first.email_storage_path) {
      transformed.push({
        ...first,
        row_type: "EMAIL",
        file_path: first.email_storage_path,
        file_name: first.email_storage_path.split("/").pop(),
      });
    }

    items.forEach((att) => {
      if (att.attachment_storage_path) {
        transformed.push({
          ...att,
          row_type: "ATTACHMENT",
          file_path: att.attachment_storage_path,
          file_name: att.attachment_storage_path.split("/").pop(),
        });
      }
    });
  });

  return transformed;
}

function extractPolicySuggestions(apiResults) {
  const seen = new Set();
  const suggestions = [];

  for (const row of apiResults) {
    const policy = row.policyno?.trim();

    if (!policy || seen.has(policy)) {
      continue;
    }

    seen.add(policy);
    suggestions.push(policy);

    if (suggestions.length >= POLICY_SUGGEST_MAX) {
      break;
    }
  }

  return suggestions;
}

export default function SearchDocuments() {
  const { instance, accounts } = useMsal();

  /* =========================
     SEARCH FIELDS
  ========================= */

  const [policyNo, setPolicyNo] = useState("");

  const [email, setEmail] = useState("");

  const [insured, setInsured] = useState("");

  const [agencyNo, setAgencyNo] = useState("");

  const FILESYSTEM = process.env.REACT_APP_ADLS_SEARCH_FILESYSTEM || "cfpuw";
  /* =========================
     DATA
  ========================= */

  const [results, setResults] = useState([]);

  const [resultCount, setResultCount] = useState(0);

  const [loading, setLoading] = useState(false);

  const [error, setError] = useState("");

  const [hasSearched, setHasSearched] = useState(false);

  const [policySuggestions, setPolicySuggestions] = useState([]);

  const [showPolicyDropdown, setShowPolicyDropdown] = useState(false);

  const [suggestLoading, setSuggestLoading] = useState(false);

  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(-1);

  const policyDropdownRef = useRef(null);

  const suggestControllerRef = useRef(null);

  const searchControllerRef = useRef(null);

  const skipSuggestFetchRef = useRef(false);

  const suggestRequestIdRef = useRef(0);

  /* =========================
     PREVIEW
  ========================= */

  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  const [previewUrl, setPreviewUrl] = useState(null);

  const [previewError, setPreviewError] = useState(null);

  const [previewLoading, setPreviewLoading] = useState(false);

  const [downloadLoading, setDownloadLoading] = useState(false);

  const [fileTooltip, setFileTooltip] = useState(null);

  const showFileTooltip = useCallback((event, fileName) => {
    if (!fileName) {
      return;
    }

    const rect = event.currentTarget.getBoundingClientRect();

    setFileTooltip({
      text: fileName,
      top: rect.top,
      left: rect.left + rect.width / 2,
    });
  }, []);

  const hideFileTooltip = useCallback(() => {
    setFileTooltip(null);
  }, []);

  useEffect(() => {
    if (!fileTooltip) {
      return undefined;
    }

    const hide = () => setFileTooltip(null);

    window.addEventListener("scroll", hide, true);
    window.addEventListener("resize", hide);

    return () => {
      window.removeEventListener("scroll", hide, true);
      window.removeEventListener("resize", hide);
    };
  }, [fileTooltip]);

  useEffect(() => {
    if (!showPolicyDropdown) {
      return undefined;
    }

    const handleClickOutside = (event) => {
      if (
        policyDropdownRef.current &&
        !policyDropdownRef.current.contains(event.target)
      ) {
        setShowPolicyDropdown(false);
        setActiveSuggestionIndex(-1);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showPolicyDropdown]);

  useEffect(() => {
    return () => {
      if (suggestControllerRef.current) {
        suggestControllerRef.current.abort();
      }

      if (searchControllerRef.current) {
        searchControllerRef.current.abort();
      }
    };
  }, []);

  /* =========================
     SEARCH
  ========================= */

  const executeSearch = useCallback(
    async (options = {}) => {
      const activePolicyNo =
        options.policyno !== undefined ? options.policyno : policyNo;
      const activeEmail =
        options.mail_from_address !== undefined
          ? options.mail_from_address
          : email;
      const activeInsured =
        options.primary_insured !== undefined
          ? options.primary_insured
          : insured;
      const activeAgencyNo =
        options.agency_no !== undefined ? options.agency_no : agencyNo;

      if (searchControllerRef.current) {
        searchControllerRef.current.abort();
      }

      const controller = new AbortController();

      searchControllerRef.current = controller;

      try {
        setError("");
        setShowPolicyDropdown(false);
        setActiveSuggestionIndex(-1);

        if (!activePolicyNo && !activeEmail && !activeInsured && !activeAgencyNo) {
          setError("Enter at least one search field");

          return;
        }

        setHasSearched(true);
        setLoading(true);
        setResults([]);
        setResultCount(0);

        const res = await callApi(
          instance,
          accounts,
          "GET",
          "/api/admin/search",
          null,
          {
            signal: controller.signal,
            params: {
              policyno: activePolicyNo
                ? normalizePolicyQueryParam(activePolicyNo)
                : activePolicyNo,
              mail_from_address: activeEmail,
              primary_insured: activeInsured,
              agency_no: activeAgencyNo,
            },
          },
        );

        if (controller.signal.aborted) {
          return;
        }

        const apiResults = res?.data?.data || [];
        const transformed = transformSearchResults(apiResults);

        setResults(transformed);
        setResultCount(
          typeof res?.data?.count === "number"
            ? res.data.count
            : transformed.length,
        );
      } catch (err) {
        if (
          err?.name === "AbortError" ||
          err?.message === "Request cancelled"
        ) {
          return;
        }

        console.error(err);

        setError(err.message || "Search failed");
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    },
    [instance, accounts, policyNo, email, insured, agencyNo],
  );

  const runSearch = () => {
    executeSearch();
  };

  const handlePolicySuggestionSelect = useCallback(
    (selectedPolicy) => {
      skipSuggestFetchRef.current = true;
      setPolicyNo(selectedPolicy);
      setPolicySuggestions([]);
      setShowPolicyDropdown(false);
      setActiveSuggestionIndex(-1);
      executeSearch({ policyno: selectedPolicy });
    },
    [executeSearch],
  );

  const fetchPolicySuggestions = useCallback(
    async (query) => {
      const trimmed = query.trim();

      if (trimmed.length < POLICY_SUGGEST_MIN_LENGTH) {
        setPolicySuggestions([]);
        setShowPolicyDropdown(false);
        setActiveSuggestionIndex(-1);
        return;
      }

      if (suggestControllerRef.current) {
        suggestControllerRef.current.abort();
      }

      const controller = new AbortController();

      suggestControllerRef.current = controller;
      const requestId = ++suggestRequestIdRef.current;

      try {
        setSuggestLoading(true);

        const res = await callApi(
          instance,
          accounts,
          "GET",
          "/api/admin/search",
          null,
          {
            signal: controller.signal,
            params: { policyno: normalizePolicyQueryParam(trimmed) },
          },
        );

        if (
          controller.signal.aborted ||
          requestId !== suggestRequestIdRef.current
        ) {
          return;
        }

        const suggestions = extractPolicySuggestions(res?.data?.data || []);

        setPolicySuggestions(suggestions);
        setShowPolicyDropdown(true);
        setActiveSuggestionIndex(-1);
      } catch (err) {
        if (
          err?.name === "AbortError" ||
          err?.message === "Request cancelled"
        ) {
          return;
        }

        console.error("Policy suggestion lookup failed", err);
        setPolicySuggestions([]);
      } finally {
        if (
          !controller.signal.aborted &&
          requestId === suggestRequestIdRef.current
        ) {
          setSuggestLoading(false);
        }
      }
    },
    [instance, accounts],
  );

  useEffect(() => {
    if (skipSuggestFetchRef.current) {
      skipSuggestFetchRef.current = false;
      return undefined;
    }

    const trimmed = policyNo.trim();

    if (!trimmed) {
      setPolicySuggestions([]);
      setShowPolicyDropdown(false);
      setActiveSuggestionIndex(-1);
      return undefined;
    }

    if (trimmed.length < POLICY_SUGGEST_MIN_LENGTH) {
      setPolicySuggestions([]);
      setShowPolicyDropdown(false);
      setActiveSuggestionIndex(-1);
      return undefined;
    }

    const timer = setTimeout(() => {
      fetchPolicySuggestions(trimmed);
    }, POLICY_SUGGEST_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [policyNo, fetchPolicySuggestions]);

  const handlePolicyInputChange = (e) => {
    setPolicyNo(e.target.value);
    setShowPolicyDropdown(true);
  };

  const handlePolicyInputKeyDown = (e) => {
    if (e.key === "Escape") {
      setShowPolicyDropdown(false);
      setActiveSuggestionIndex(-1);
      return;
    }

    if (e.key === "ArrowDown") {
      if (!showPolicyDropdown || policySuggestions.length === 0) {
        return;
      }

      e.preventDefault();
      setActiveSuggestionIndex((prev) =>
        prev < policySuggestions.length - 1 ? prev + 1 : 0,
      );
      return;
    }

    if (e.key === "ArrowUp") {
      if (!showPolicyDropdown || policySuggestions.length === 0) {
        return;
      }

      e.preventDefault();
      setActiveSuggestionIndex((prev) =>
        prev > 0 ? prev - 1 : policySuggestions.length - 1,
      );
      return;
    }

    if (e.key === "Enter") {
      if (showPolicyDropdown && policySuggestions.length > 0) {
        e.preventDefault();
        const index =
          activeSuggestionIndex >= 0 ? activeSuggestionIndex : 0;
        handlePolicySuggestionSelect(policySuggestions[index]);
        return;
      }

      e.preventDefault();
      runSearch();
    }
  };

  /* =========================
     DOWNLOAD
  ========================= */

  const handleDownload = useCallback(async (path) => {
    /*
    Prevent rapid spam downloads
  */

    if (downloadLoading) {
      return;
    }

    const controller = new AbortController();

    try {
      setDownloadLoading(true);

      const cleaned = path?.trim();

      if (!cleaned) {
        return;
      }

      const { filesystem, path: filePath } = resolveAdlsPath(
        cleaned,
        FILESYSTEM,
      );

      const baseUrl =
        process.env.REACT_APP_API_BASE_URL || "http://localhost:5000";

      /*
      TOKEN
    */

      const tokenResponse = await instance.acquireTokenSilent({
        account: instance.getActiveAccount(),
      });

      /*
      DOWNLOAD
    */

      const response = await fetch(
        `${baseUrl}/api/adls/download` +
          `?filesystem=${encodeURIComponent(filesystem)}` +
          `&path=${encodeURIComponent(filePath)}`,
        {
          method: "GET",

          signal: controller.signal,

          headers: {
            Authorization: `Bearer ${tokenResponse.accessToken}`,
          },
        },
      );

      if (!response.ok) {
        throw new Error("Download failed");
      }

      const blob = await response.blob();

      /*
      Empty response protection
    */

      if (!blob || blob.size === 0) {
        throw new Error("Empty download response");
      }

      /*
      FILENAME
    */

      const fileName = filePath.split("/").pop() || "download";

      /*
      DOWNLOAD
    */

      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement("a");

      link.href = blobUrl;

      link.download = fileName;

      document.body.appendChild(link);

      link.click();

      link.remove();

      /*
      Cleanup
    */

      setTimeout(() => {
        window.URL.revokeObjectURL(blobUrl);
      }, 60000);
    } catch (err) {
      if (err?.name === "AbortError" || err?.message === "Request cancelled") {
        return;
      }

      console.error("Download failed", err);

      setError(err.message || "Download failed");
    } finally {
      controller.abort();

      setDownloadLoading(false);
    }
  }, [instance, FILESYSTEM, downloadLoading]);

  /* =========================
     PREVIEW
  ========================= */

  const handlePreview = useCallback(async (path) => {
    /*
    Prevent rapid preview spam
  */

    if (previewLoading) {
      return;
    }

    const controller = new AbortController();

    try {
      setPreviewLoading(true);

      setIsPreviewOpen(true);

      setPreviewError(null);

      /*
      Cleanup previous blob URL
    */

      if (previewUrl && previewUrl.startsWith("blob:")) {
        URL.revokeObjectURL(previewUrl);
      }

      setPreviewUrl(null);

      const cleaned = path?.trim();

      if (!cleaned) {
        throw new Error("Invalid path");
      }

      const { filesystem, path: filePath } = resolveAdlsPath(
        cleaned,
        FILESYSTEM,
      );

      const lower = filePath.toLowerCase();

      const isTiff = lower.endsWith(".tif") || lower.endsWith(".tiff");

      const isEml = lower.endsWith(".eml");

      const isHeic = lower.endsWith(".heic") || lower.endsWith(".heif");

      const baseUrl =
        process.env.REACT_APP_API_BASE_URL || "http://localhost:5000";

      const BACKEND_CLIENT_ID = process.env.REACT_APP_BACKEND_CLIENT_ID;

      /* =========================
       TIFF
    ========================= */

      if (isTiff || isEml || isHeic) {
        const token = await instance.acquireTokenSilent({
          account: instance.getActiveAccount() || accounts?.[0],
          scopes: [`api://${BACKEND_CLIENT_ID}/access_as_user`],
        });

        const response = await fetch(
          `${baseUrl}/api/adls/preview-blob` +
            `?filesystem=${encodeURIComponent(filesystem)}` +
            `&path=${encodeURIComponent(filePath)}`,
          {
            method: "GET",
            signal: controller.signal,
            headers: {
              Authorization: `Bearer ${token.accessToken}`,
            },
          },
        );

        if (!response.ok) {
          throw new Error(`Failed to preview file (${response.status})`);
        }

        const blob = await response.blob();

        const blobUrl = URL.createObjectURL(blob);

        setPreviewUrl(blobUrl);

        return;
      }

      /* =========================
       NORMAL FILES
    ========================= */

      const res = await callApi(
        instance,
        accounts,
        "GET",
        "/api/adls/preview-url",
        null,
        {
          signal: controller.signal,

          params: {
            filesystem,
            path: filePath,
          },
        },
      );

      if (!res?.data?.url) {
        throw new Error("Preview URL missing");
      }

      setPreviewUrl(res.data.url);
    } catch (err) {
      /*
      Ignore cancellation
    */

      if (err?.name === "AbortError" || err?.message === "Request cancelled") {
        return;
      }

      console.error("Preview failed", err);

      setPreviewError(err.message || "Preview failed");
    } finally {
      controller.abort();

      setPreviewLoading(false);
    }
  }, [instance, accounts, FILESYSTEM, previewLoading, previewUrl]);

  const closePreview = () => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }

    setIsPreviewOpen(false);

    setPreviewUrl(null);

    setPreviewError(null);
  };

  /* =========================
     RESPONSIVE CARD MAPPERS
  ========================= */

  const mapDocCard = useCallback(
    (r) => {
      return {
        id: `doc-${r.msg_id}-${r.file_name}`,
        icon:
          r.row_type === "EMAIL" ? <Mail size={16} /> : <FileText size={16} />,
        tone: r.row_type === "EMAIL" ? "info" : "success",
        title: r.policyno || "—",
        subtitle: r.email_subject || "—",
        meta: [
          {
            label: "Type",
            value: r.document_type || "—",
          },
          {
            label: "Date",
            value: r.verified_at
              ? new Date(r.verified_at).toLocaleDateString()
              : "—",
          },
          { label: "Mail From", value: r.mail_from_address || "—" },
          { label: "File", value: r.file_name || "—" },
        ],
        badges: [
          {
            value: r.row_type === "EMAIL" ? "Email" : "Attachment",
            tone: r.row_type === "EMAIL" ? "info" : "success",
          },
        ],
        actions: [
          {
            label: "Preview",
            icon: <Eye size={14} />,
            onClick: () => handlePreview(r.file_path),
          },
        ...(r.effective_access === "WRITE"
          ? [
              {
                label: "Download",
                icon: <Download size={14} />,
                onClick: () => handleDownload(r.file_path),
              },
            ]
          : []),
        ],
      };
    },
    [handlePreview, handleDownload],
  );

  const mapDocDrawer = useCallback(
    (r) => ({
      title: r.policyno || r.email_subject || "—",
      fields: [
        { label: "Policy Number", value: r.policyno || "—" },
        { label: "Mail From", value: r.mail_from_address || "—" },
        { label: "Document Type", value: r.document_type || "—" },
        { label: "Email Subject", value: r.email_subject || "—" },
        {
          label: "Date",
          value: r.verified_at
            ? new Date(r.verified_at).toLocaleString()
            : "—",
        },
      ],
      attachments: [
        {
          label: r.file_name || "Document",
          onPreview: () => handlePreview(r.file_path),
          ...(r.effective_access === "WRITE"
            ? { onDownload: () => handleDownload(r.file_path) }
            : {}),
        },
      ],
    }),
    [handlePreview, handleDownload],
  );

  const renderTable = useCallback(
    () => {
      if (loading) return <div style={styles.empty}>Searching...</div>;
      if (results.length === 0)
        return <div style={styles.empty}>No results found</div>;
      return (
        <ScrollableTable style={styles.tableWrap}>
        <table style={styles.table}>
          <colgroup>
            <col style={{ width: "14%" }} />
            <col style={{ width: "17%" }} />
            <col style={{ width: "11%" }} />
            <col style={{ width: "9%" }} />
            <col style={{ width: "30%" }} />
            <col style={{ width: "19%" }} />
          </colgroup>
          <thead>
            <tr>
              <th style={styles.th}>POLICY NUMBER</th>

              <th style={styles.th}>MAIL FROM</th>

              <th style={styles.th}>DOCUMENT TYPE</th>

              <th style={styles.th}>DATE</th>

              <th style={styles.th}>FILE</th>

              <th style={styles.th}>ACTIONS</th>
            </tr>
          </thead>

          <tbody>
            {results.map((r, index) => (
              <tr key={`${r.msg_id}-${index}`} style={styles.row}>
                <td style={styles.td}>
                  <span style={styles.policyText}>{r.policyno || "—"}</span>
                </td>

                <td style={styles.td}>
                  <span style={styles.mailText}>{r.mail_from_address || "—"}</span>
                </td>

                <td style={styles.td}>
                  <span
                    style={
                      r.row_type === "EMAIL"
                        ? styles.emailBadge
                        : styles.attachmentBadge
                    }
                  >
                    {r.document_type}
                  </span>
                </td>

                <td style={styles.td}>
                  <span style={styles.dateText}>
                    {r.verified_at
                      ? new Date(r.verified_at).toLocaleDateString()
                      : "—"}
                  </span>
                </td>

                <td style={styles.td}>
                  <span
                    style={styles.fileBadgeWrap}
                    onMouseEnter={(e) => showFileTooltip(e, r.file_name)}
                    onMouseLeave={hideFileTooltip}
                  >
                    <span style={styles.fileBadge}>{r.file_name}</span>
                  </span>
                </td>

                <td style={styles.td}>
                  <div style={styles.actionRow}>
                    <button
                      title="Preview document"
                      style={styles.previewButton}
                      onClick={() => handlePreview(r.file_path)}
                    >
                      Preview
                    </button>

                    {r.effective_access === "WRITE" && (
                      <button
                        title="Download document"
                        style={styles.downloadButton}
                        onClick={() => handleDownload(r.file_path)}
                      >
                        Download
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </ScrollableTable>
      );
    },
    [results, loading, handlePreview, handleDownload, showFileTooltip, hideFileTooltip],
  );

  /* =========================
     UI
  ========================= */

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={styles.header}>
          <div>
            <h2 style={styles.title}>Search Documents</h2>

            <p style={styles.sub}>
              Search verified classification documents and attachments.
            </p>
          </div>
        </div>

        {/* SEARCH */}

        <div style={styles.searchCard}>
          <div style={styles.searchRow}>
            <div style={styles.field}>
              <label style={styles.label} htmlFor="search-policy-number">
                Policy Number
              </label>

              <div ref={policyDropdownRef} style={styles.searchableWrapper}>
                <input
                  id="search-policy-number"
                  type="text"
                  role="combobox"
                  aria-autocomplete="list"
                  aria-expanded={showPolicyDropdown}
                  aria-controls="policy-suggestion-list"
                  style={styles.input}
                  placeholder="e.g. CFP 0975909293 or 097"
                  value={policyNo}
                  onFocus={() => {
                    if (policySuggestions.length > 0) {
                      setShowPolicyDropdown(true);
                    }
                  }}
                  onChange={handlePolicyInputChange}
                  onKeyDown={handlePolicyInputKeyDown}
                />

                {showPolicyDropdown && (
                  <div
                    id="policy-suggestion-list"
                    role="listbox"
                    style={styles.policyDropdown}
                    className="scrollable"
                  >
                    {suggestLoading ? (
                      <div style={styles.policyOptionEmpty}>
                        Loading suggestions...
                      </div>
                    ) : policySuggestions.length === 0 ? (
                      <div style={styles.policyOptionEmpty}>
                        No policies found
                      </div>
                    ) : (
                      policySuggestions.map((suggestion, index) => (
                        <div
                          key={suggestion}
                          role="option"
                          aria-selected={activeSuggestionIndex === index}
                          style={{
                            ...styles.policyOption,
                            ...(activeSuggestionIndex === index
                              ? styles.policyOptionActive
                              : null),
                          }}
                          onMouseEnter={() => setActiveSuggestionIndex(index)}
                          onClick={() =>
                            handlePolicySuggestionSelect(suggestion)
                          }
                        >
                          {suggestion}
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>

              <span style={styles.fieldFooter} aria-live="polite">
                {suggestLoading ? "Loading suggestions..." : ""}
              </span>
            </div>

            <div style={styles.field}>
              <label style={styles.label}>Mail From</label>

              <input
                style={styles.input}
                placeholder="Enter email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />

              <span style={styles.fieldFooter} aria-hidden="true" />
            </div>

            <div style={styles.field}>
              <label style={styles.label}>Primary Insured</label>

              <input
                style={styles.input}
                placeholder="Enter insured"
                value={insured}
                onChange={(e) => setInsured(e.target.value)}
              />

              <span style={styles.fieldFooter} aria-hidden="true" />
            </div>

            <div style={styles.field}>
              <label style={styles.label}>Agency Number</label>

              <input
                style={styles.input}
                placeholder="Enter agency number"
                value={agencyNo}
                onChange={(e) => setAgencyNo(e.target.value)}
              />

              <span style={styles.fieldFooter} aria-hidden="true" />
            </div>

            <div style={styles.buttonField}>
              <label style={styles.labelHidden} aria-hidden="true">
                Search
              </label>

              <button
                style={styles.button}
                onClick={runSearch}
                disabled={loading}
              >
                {loading ? "Searching..." : "Search"}
              </button>

              <span style={styles.fieldFooter} aria-hidden="true" />
            </div>
          </div>
        </div>

        {/* ERROR */}

        {error && <div style={styles.error}>{error}</div>}

        {/* RESULTS */}

        {hasSearched && (
          <div style={{ marginTop: "24px" }}>
            {!loading && (
              <div style={styles.resultCount} aria-live="polite">
                {resultCount}{" "}
                {resultCount === 1 ? "result" : "results"}
              </div>
            )}

            <ResponsiveTableCards
              data={results}
              cardMapper={mapDocCard}
              drawerMapper={mapDocDrawer}
              renderTable={renderTable}
              loading={loading}
              emptyTitle="No results found"
              emptySub="Try adjusting your search criteria."
            />
          </div>
        )}
      </div>

      {/* PREVIEW */}

      {fileTooltip &&
        createPortal(
          <div
            style={{
              ...styles.fileTooltip,
              top: fileTooltip.top,
              left: fileTooltip.left,
            }}
            role="tooltip"
          >
            {fileTooltip.text}
            <span style={styles.fileTooltipArrow} aria-hidden />
          </div>,
          document.body,
        )}

      {isPreviewOpen && (
        <div style={styles.modalBackdrop}>
          <div style={styles.modal}>
            <div style={styles.modalHeader}>
              <span>Preview</span>

              <button onClick={closePreview} style={styles.closeButton}>
                ✕
              </button>
            </div>

            <div style={styles.modalBody} className="scrollable">
              {previewError && <div>{previewError}</div>}
              {previewUrl && (
                <iframe
                  title="Preview"
                  src={previewUrl}
                  style={{
                    width: "100%",
                    height: "100%",
                    border: "none",
                  }}
                />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* =========================
   STYLES
========================= */

const styles = {
  container: {
    // padding: "clamp(12px, 4vw, 24px)",
    // background: "#f8fafc",
    minHeight: "100vh",
    boxSizing: "border-box",
    maxWidth: "100%",
    // overflowX: "hidden",
  },

  readBadge: {
    background: "#eff6ff",
    color: "#1d4ed8",
    padding: "6px 12px",
    borderRadius: "999px",
    fontSize: "11px",
    fontWeight: "700",
    letterSpacing: "0.04em",
  },

  writeBadge: {
    background: "#dcfce7",
    color: "#166534",
    padding: "6px 12px",
    borderRadius: "999px",
    fontSize: "11px",
    fontWeight: "700",
    letterSpacing: "0.04em",
  },

  card: {
    background: "#ffffff",
    borderRadius: "16px",
    padding: "clamp(16px, 5vw, 24px)",
    border: "1px solid #e2e8f0",
    boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
    width: "100%",
    boxSizing: "border-box",
  },

  header: {
    marginBottom: "20px",
  },

  title: {
    margin: 0,
    fontSize: "28px",
    fontWeight: "700",
    color: "#0f172a",
  },

  sub: {
    color: "#64748b",
    marginTop: "6px",
    fontSize: "14px",
  },

  searchCard: {
    background: "#f8fafc",
    border: "1px solid #e2e8f0",
    borderRadius: "14px",
    padding: "clamp(12px, 4vw, 18px)",
    width: "100%",
    boxSizing: "border-box",
  },

  searchRow: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: "clamp(12px, 3vw, 16px)",
    alignItems: "start",
    width: "100%",
    boxSizing: "border-box",
  },

  field: {
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
    width: "100%",
    boxSizing: "border-box",
  },

  searchableWrapper: {
    position: "relative",
    width: "100%",
  },

  policyDropdown: {
    position: "absolute",
    top: "100%",
    left: 0,
    right: 0,
    background: "#fff",
    border: "1px solid #dbe2ea",
    borderRadius: "10px",
    marginTop: "6px",
    maxHeight: "220px",
    overflowY: "auto",
    overflowX: "hidden",
    zIndex: 1000,
    boxShadow: "0 10px 25px rgba(15,23,42,0.08)",
  },

  policyOption: {
    padding: "12px 14px",
    cursor: "pointer",
    borderBottom: "1px solid #f1f5f9",
    fontSize: "14px",
    color: "#0f172a",
    textAlign: "left",
  },

  policyOptionActive: {
    background: "#f8fafc",
  },

  policyOptionEmpty: {
    padding: "12px 14px",
    color: "#64748b",
    fontSize: "13px",
    textAlign: "left",
  },

  resultCount: {
    marginBottom: "12px",
    fontSize: "13px",
    fontWeight: "600",
    color: "#475569",
  },

  buttonField: {
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
    width: "100%",
    boxSizing: "border-box",
  },

  label: {
    fontSize: "12px",
    fontWeight: "600",
    marginBottom: "6px",
    color: "#475569",
    textTransform: "uppercase",
  },

  input: {
    padding: "12px 14px",
    border: "1px solid #cbd5e1",
    borderRadius: "10px",
    fontSize: "14px",
    background: "#fff",
    outline: "none",
    width: "100%",
    boxSizing: "border-box",
  },

  labelHidden: {
    fontSize: "12px",
    fontWeight: "600",
    marginBottom: "6px",
    color: "transparent",
    textTransform: "uppercase",
    userSelect: "none",
  },

  fieldFooter: {
    display: "block",
    marginTop: "4px",
    fontSize: "11px",
    lineHeight: "16px",
    minHeight: "16px",
    color: "#64748b",
  },

  button: {
    background: "#0f172a",
    color: "#fff",
    border: "none",
    padding: "12px 20px",
    borderRadius: "10px",
    cursor: "pointer",
    fontWeight: "600",
    height: "46px",
    width: "100%",
    boxSizing: "border-box",
  },

  error: {
    marginTop: "20px",
    background: "#fee2e2",
    color: "#991b1b",
    padding: "12px",
    borderRadius: "10px",
  },

  tableWrap: {
    overflowX: "auto",
  },

  table: {
    width: "100%",
    borderCollapse: "collapse",
    tableLayout: "fixed",
  },

  th: {
    textAlign: "center",
    whiteSpace: "nowrap",
    padding: "10px 8px",
    background: "#f8fafc",
    borderBottom: "1px solid #e2e8f0",
    fontSize: "11px",
    color: "#64748b",
    fontWeight: "600",
  },

  td: {
    padding: "10px 8px",
    borderBottom: "1px solid #f1f5f9",
    verticalAlign: "middle",
    overflow: "hidden",
    fontSize: "13px",
    textAlign: "center",
  },

  row: {
    transition: "background 0.2s ease",
  },

  emailBadge: {
    background: "#dbeafe",
    color: "#1d4ed8",
    padding: "6px 10px",
    borderRadius: "999px",
    fontSize: "11px",
    fontWeight: "600",
  },

  attachmentBadge: {
    background: "#ede9fe",
    color: "#6d28d9",
    padding: "6px 10px",
    borderRadius: "999px",
    fontSize: "11px",
    fontWeight: "600",
  },

  policyText: {
    fontSize: "13px",
    fontWeight: "600",
    color: "#0f172a",
  },

  cellText: {
    fontSize: "13px",
    color: "#334155",
    wordBreak: "break-word",
  },

  docBlock: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
  },

  docType: {
    fontSize: "13px",
    fontWeight: "600",
    color: "#0f172a",
  },

  verifyText: {
    fontSize: "12px",
    color: "#64748b",
  },

  dateText: {
    fontSize: "13px",
    color: "#475569",
  },

  subjectCell: {
    fontSize: "13px",
    lineHeight: 1.4,
    fontWeight: "400",
    marginBottom: "6px",
    wordBreak: "break-word",
    overflowWrap: "anywhere",
  },

  mailText: {
    fontSize: "12px",
    color: "#64748b",
    display: "inline-block",
    maxWidth: "100%",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    verticalAlign: "middle",
  },

  fileBadgeWrap: {
    position: "relative",
    display: "inline-block",
    maxWidth: "100%",
  },

  fileBadge: {
    background: "#f1f5f9",
    padding: "6px 8px",
    borderRadius: "8px",
    fontSize: "12px",
    display: "inline-block",
    maxWidth: "100%",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    verticalAlign: "middle",
  },

  fileTooltip: {
    position: "fixed",
    transform: "translate(-50%, calc(-100% - 10px))",
    background: "#1e293b",
    color: "#f8fafc",
    padding: "8px 12px",
    borderRadius: "10px",
    fontSize: "12px",
    fontWeight: "500",
    lineHeight: "1.5",
    whiteSpace: "normal",
    wordBreak: "break-all",
    maxWidth: "260px",
    minWidth: "120px",
    textAlign: "center",
    boxShadow: "0 6px 20px rgba(15,23,42,0.18)",
    zIndex: 10000,
    pointerEvents: "none",
  },

  fileTooltipArrow: {
    position: "absolute",
    bottom: "-4px",
    left: "50%",
    transform: "translateX(-50%) rotate(45deg)",
    width: "8px",
    height: "8px",
    background: "#1e293b",
    borderRadius: "1px",
  },

  actionRow: {
    display: "flex",
    gap: "8px",
    justifyContent: "center",
    flexWrap: "nowrap",
    whiteSpace: "nowrap",
  },

  previewButton: {
    background: "#0f766e",
    color: "#fff",
    border: "none",
    padding: "6px 8px",
    borderRadius: "8px",
    cursor: "pointer",
    fontSize: "12px",
    fontWeight: "500",
  },

  downloadButton: {
    background: "#475569",
    color: "#fff",
    border: "none",
    padding: "6px 8px",
    borderRadius: "8px",
    cursor: "pointer",
    fontSize: "12px",
    fontWeight: "500",
  },

  empty: {
    textAlign: "center",
    padding: "40px",
    color: "#64748b",
  },

  modalBackdrop: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
  },

  modal: {
    width: "90%",
    height: "90%",
    background: "#fff",
    borderRadius: "16px",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
  },

  modalHeader: {
    padding: "16px",
    borderBottom: "1px solid #e2e8f0",
    display: "flex",
    justifyContent: "space-between",
    fontWeight: "600",
  },

  modalBody: {
    flex: 1,
    overflow: "auto",
    padding: "16px",
  },

  closeButton: {
    border: "none",
    background: "transparent",
    fontSize: "18px",
    cursor: "pointer",
  },
};
